// Renders a voicing as a clean fretboard diagram (Ted Greene style).
// Pure presentational SVG: 6 strings, sounding strings carry notes,
// the root is a filled circle, other tones are hollow, with chord-tone labels
// beneath and an optional lead-note label.

import { useMemo } from 'react';
import { fretNumberMetrics, type DiagramMarkers } from '@/state/displayScale';
import { chordToneName, type ChordTypeId } from '@/music/chords';
import { pitchClassOf, sharpName } from '@/music/notes';
import { spellVoicingMidis } from '@/music/chordSpelling';
import {
  activeStringsForFingering,
  frettedMidi,
  pitchClassAtFret,
  topStringForFingering,
  type StringSet,
} from '@/music/tuning';
import { hasFlatNineAvoidInterval, type Fingering } from '@/music/voicing';

const W = 200;
const BASE_H = 240;
// Reserve enough left gutter for a right-aligned two-digit starting fret.
// The matching right-margin reduction keeps the fretboard itself the same width.
const MARGIN = { top: 35, bottom: 20, left: 40, right: 17 };
// Leave the low-E marker's full radius clear when Drop 3 uses the sixth string.
const FRET_LABEL_GAP = 15;
/**
 * Where the fretboard's centre sits across the diagram's viewBox, 0..1.
 *
 * The margins are asymmetric — the left one carries the fret-position number —
 * so the drawn grid is NOT centred in the box. Anything that wants to line up
 * with the diagram optically (a label above it) must use this, not 0.5.
 */
export const FRETBOARD_CENTER_RATIO = (MARGIN.left + (W - MARGIN.right)) / 2 / W;
const NUM_FRETS = 5;

export interface ChordDiagramProps {
  fingering: Fingering;
  rootDisplay: string;
  chordType?: ChordTypeId;
  stringSet: StringSet;
  title?: string;
  subtitle?: string;
  /** Show the chord title inside the SVG (off when a header sits above it). */
  showTitle?: boolean;
  leadNote?: string | null;
  showIntervals?: boolean;
  /** Arrange label size; omitted for other diagram contexts. */
  fretNumberScale?: number;
  markers?: DiagramMarkers;
  highlightAvoid?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Position and size when the diagram is nested inside a larger SVG. */
  viewport?: { x: number; y: number; width: number; height: number };
}

export function ChordDiagram({
  fingering,
  rootDisplay,
  chordType,
  stringSet,
  title,
  subtitle,
  showTitle = true,
  leadNote,
  showIntervals = true,
  fretNumberScale,
  markers = 'dots',
  highlightAvoid = false,
  className,
  ariaLabel,
  viewport,
}: ChordDiagramProps) {
  const model = useMemo(() => {
    const strings = activeStringsForFingering(fingering);
    const used = strings
      .map((s) => fingering.frets[s])
      .filter((f): f is number => f !== null && f !== undefined && f > 0);
    const minFret = used.length > 0 ? Math.max(0, Math.min(...used) - 1) : 0;
    const height = BASE_H + (leadNote ? 14 : 0);
    const fretboardW = W - MARGIN.left - MARGIN.right;
    const fretboardH = BASE_H - MARGIN.top - MARGIN.bottom;
    const stringSpacing = fretboardW / 5;
    const numFrets = Math.max(NUM_FRETS, ...used.map((fret) => fret - minFret));
    const fretSpacing = fretboardH / numFrets;
    const rootPc = pitchClassOf(rootDisplay);
    const voicedStrings = strings.filter((string) => fingering.frets[string] != null);
    const midis = voicedStrings.map((string) => frettedMidi(string, fingering.frets[string]!));
    const spelled = chordType
      ? spellVoicingMidis(midis, rootDisplay, chordType, sharpName)
      : midis.map((midi) => sharpName(midi % 12));
    const namesByString = new Map(voicedStrings.map((string, index) => [string, spelled[index]]));

    const notes = strings.map((stringIdx) => {
      const fret = fingering.frets[stringIdx];
      if (fret === null || fret === undefined) return null;
      const x = MARGIN.left + stringIdx * stringSpacing;
      const y = fret === 0 ? MARGIN.top - 12 : MARGIN.top + (fret - minFret - 0.5) * fretSpacing;
      return {
        stringIdx,
        x,
        y,
        isRoot: pitchClassAtFret(stringIdx, fret) === rootPc,
        note: namesByString.get(stringIdx)!.replace(/#/g, '♯').replace(/b/g, '♭'),
        interval: chordToneName(pitchClassAtFret(stringIdx, fret), rootPc, chordType),
      };
    });

    return {
      minFret,
      height,
      fretboardW,
      fretboardH,
      stringSpacing,
      fretSpacing,
      numFrets,
      notes,
    };
  }, [fingering, rootDisplay, chordType, leadNote]);

  const avoid = highlightAvoid && hasFlatNineAvoidInterval(fingering, stringSet);
  const { minFret, height, fretboardW, fretboardH, stringSpacing, fretSpacing, numFrets, notes } =
    model;
  // When the title is rendered above the SVG (guitar sheet), the reserved title
  // band at the top is dead space that reads as a gap before the fretboard.
  // Crop it from the viewBox — by a FIXED amount that keeps the open-marker
  // band whether or not this voicing uses it. Cropping 13 vs 28 by content
  // gave cards different intrinsic aspects, so identical CSS caps rendered
  // neighbouring diagrams at visibly different sizes ("every box a different
  // shape"). One aspect for every untitled card; markers land in the reserved
  // band when present.
  const topCrop = showTitle ? 0 : 13;
  const wideFretLabel = minFret + 1 >= 10;
  const fretLabel =
    fretNumberScale === undefined ? undefined : fretNumberMetrics(fretNumberScale, wideFretLabel);

  if (fingering.unavailable) {
    const reason = fingering.unavailable === 'melody' ? 'for this melody' : 'for this chord';
    return (
      <svg
        className={`chord-diagram${className ? ` ${className}` : ''}`}
        data-voicing-family={fingering.voicingFamily}
        data-voicing-unavailable={fingering.unavailable}
        x={viewport?.x}
        y={viewport?.y}
        width={viewport?.width}
        height={viewport?.height}
        style={viewport ? { width: viewport.width, height: viewport.height } : undefined}
        viewBox={`0 ${topCrop} ${W} ${height - topCrop}`}
        role="img"
        aria-label={`${title ?? ariaLabel ?? rootDisplay}: no matching Joe Pass shape ${reason}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {showTitle && title && (
          <text x={W / 2} y={15} className="cd-title">
            {title}
          </text>
        )}
        <text x={W / 2} y={BASE_H / 2 - 12} className="cd-interval" style={{ fontSize: 28 }}>
          <tspan x={W / 2}>No shape</tspan>
          <tspan x={W / 2} dy={30} style={{ fontSize: 23 }}>
            {fingering.unavailable === 'melody' ? 'for melody' : 'for chord'}
          </tspan>
        </text>
      </svg>
    );
  }

  return (
    <svg
      className={`chord-diagram${avoid ? ' is-avoid' : ''}${className ? ` ${className}` : ''}`}
      data-voicing-family={fingering.voicingFamily}
      data-source-shape-id={fingering.sourceShapeId}
      data-diagram-markers={markers}
      x={viewport?.x}
      y={viewport?.y}
      width={viewport?.width}
      height={viewport?.height}
      style={viewport ? { width: viewport.width, height: viewport.height } : undefined}
      viewBox={`0 ${topCrop} ${W} ${height - topCrop}`}
      role="img"
      aria-label={ariaLabel ?? title ?? 'chord diagram'}
      preserveAspectRatio="xMidYMid meet"
    >
      {showTitle && title && (
        <text x={W / 2} y={15} className="cd-title">
          {title}
          {subtitle ? ` ${subtitle}` : ''}
        </text>
      )}

      {/* Strings (vertical) */}
      {Array.from({ length: 6 }, (_, i) => {
        const x = MARGIN.left + i * stringSpacing;
        return (
          <line
            key={`s${i}`}
            x1={x}
            y1={MARGIN.top}
            x2={x}
            y2={MARGIN.top + fretboardH}
            className="cd-string"
          />
        );
      })}

      {/* Frets (horizontal) */}
      {Array.from({ length: numFrets + 1 }, (_, i) => {
        const y = MARGIN.top + i * fretSpacing;
        const isNut = i === 0 && minFret === 0;
        return (
          <line
            key={`f${i}`}
            x1={MARGIN.left}
            y1={y}
            x2={MARGIN.left + fretboardW}
            y2={y}
            className={isNut ? 'cd-nut' : 'cd-fret'}
          />
        );
      })}

      {/* Starting fret label, right-aligned beside the fretboard and centred
          on the first fret row. Arrange keeps both digit counts at one height. */}
      {minFret > 0 && (
        <text
          x={MARGIN.left - FRET_LABEL_GAP}
          y={MARGIN.top + fretSpacing * 0.5}
          className={`cd-fret-label${wideFretLabel ? ' cd-fret-label--wide' : ''}`}
          style={fretLabel ? { fontSize: fretLabel.fontSize } : undefined}
          textLength={fretLabel?.textLength ?? (wideFretLabel ? 24 : undefined)}
          lengthAdjust={fretLabel || wideFretLabel ? 'spacingAndGlyphs' : undefined}
        >
          {minFret + 1}
        </text>
      )}

      {fingering.voicingFamily === 'joe-pass' &&
        fingering.frets.map((fret, string) =>
          fret == null ? (
            <text
              key={`mute${string}`}
              x={MARGIN.left + string * stringSpacing}
              y={MARGIN.top - 8}
              className="cd-interval"
              aria-label={`String ${6 - string} muted`}
            >
              ×
            </text>
          ) : null,
        )}

      {/* The same sounding positions in each mode; labels replace the dots. */}
      {notes.map((note, i) => {
        if (!note) return null;
        if (markers === 'dots')
          return (
            <circle
              key={`n${i}`}
              cx={note.x}
              cy={note.y}
              r={10}
              className={note.isRoot ? 'cd-note cd-note-root' : 'cd-note'}
            />
          );
        const label = markers === 'notes' ? note.note : note.interval;
        const parts = label.match(/^([♭♯]*)([A-GR]|\d+)([♭♯]*)$/);
        const long = label.length > 2;
        // Keep the complete circle visible above the nut for open strings.
        const markerY = Math.max(note.y, topCrop + 15);
        const markerX = note.x;
        return (
          <g key={`n${i}`} className={`cd-label-marker${note.isRoot ? ' is-root' : ''}`}>
            <circle cx={markerX} cy={markerY} r={14} className="cd-marker-background" />
            <text
              x={markerX}
              y={markerY}
              className={`cd-marker-label${note.isRoot ? ' cd-marker-root' : ''}${long ? ' is-long' : ''}`}
              data-marker-string={note.stringIdx}
              data-marker-label={markers}
              data-marker-note={note.note}
              data-marker-interval={note.interval}
              data-marker-root={note.isRoot || undefined}
              style={{ fontFamily: 'Arial, sans-serif' }}
            >
              {parts ? (
                <>
                  {parts[1] && (
                    <tspan className="cd-marker-accidental" style={{ fontFamily: 'Bravura' }}>
                      {parts[1]}
                    </tspan>
                  )}
                  <tspan
                    className="cd-marker-value"
                    style={{ fontFamily: 'inherit' }}
                    dx={parts[1] ? 0.5 : undefined}
                  >
                    {parts[2]}
                  </tspan>
                  {parts[3] && (
                    <tspan
                      className="cd-marker-accidental"
                      style={{ fontFamily: 'Bravura' }}
                      dx={0.5}
                    >
                      {parts[3]}
                    </tspan>
                  )}
                </>
              ) : (
                label
              )}
            </text>
          </g>
        );
      })}

      {/* Interval labels */}
      {showIntervals &&
        markers === 'dots' &&
        notes.map((note, i) =>
          note ? (
            <text
              key={`i${i}`}
              x={MARGIN.left + note.stringIdx * stringSpacing}
              y={MARGIN.top + fretboardH + 15}
              className="cd-interval"
            >
              {note.interval}
            </text>
          ) : null,
        )}

      {/* Lead-note label under the top string */}
      {leadNote && (
        <text
          x={MARGIN.left + topStringForFingering(fingering) * stringSpacing}
          y={MARGIN.top + fretboardH + 31}
          className="cd-lead"
        >
          {leadNote}
        </text>
      )}
    </svg>
  );
}
